import { useEffect, useState } from "react";
import { Search, Trash2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle, TH, TD, Badge } from "./AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";

export default function Servers() {
  const [guilds, setGuilds] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get(`/admin/guilds${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setGuilds(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/guilds/${id}`, body);
      toast.success("Server updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (g) => {
    if (!window.confirm(`Remove GrooveMusic from ${g.name}?`)) return;
    try {
      await api.delete(`/admin/guilds/${g.id}`);
      toast.success(`Removed from ${g.name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openEdit = (g) => {
    setEditing(g);
    setForm({ prefix: g.prefix, dj_role: g.dj_role, music_channel: g.music_channel, default_volume: g.default_volume });
  };

  return (
    <div data-testid="admin-servers">
      <PageTitle title="Server Management" sub={`${guilds.length} Discord servers using GrooveMusic — synced live from the bot.`} />
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative max-w-sm mb-6">
        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search servers…" data-testid="servers-search"
          className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple" />
      </form>

      <div className="rounded-lg border border-white/5 bg-panel overflow-x-auto">
        <table className="w-full min-w-[900px]" data-testid="servers-table">
          <thead className="border-b border-white/5"><tr><TH>Server</TH><TH>Owner</TH><TH>Members</TH><TH>Tier</TH><TH>Session</TH><TH>Commands</TH><TH>Enabled</TH><TH /></tr></thead>
          <tbody className="divide-y divide-white/5">
            {guilds.map((g) => (
              <tr key={g.id} className={g.removed ? "opacity-40" : ""} data-testid={`server-row-${g.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <TD className="font-semibold text-white">{g.name}</TD>
                <TD>{g.owner}</TD>
                <TD className="font-mono">{g.members.toLocaleString()}</TD>
                <TD>{g.premium ? <Badge tone="amber">Premium</Badge> : <Badge tone="grey">Free</Badge>}</TD>
                <TD>{g.active_session ? <Badge tone="green">Playing</Badge> : <Badge tone="grey">Idle</Badge>}</TD>
                <TD className="font-mono">{g.commands_used.toLocaleString()}</TD>
                <TD>
                  <Switch checked={!g.disabled} onCheckedChange={(v) => patch(g.id, { disabled: !v })} data-testid={`server-toggle-${g.id}`} />
                </TD>
                <TD>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(g)} className="p-2 text-white/40 hover:text-white transition-colors duration-150" data-testid={`server-config-${g.id}`} aria-label="Configure">
                      <SlidersHorizontal size={15} />
                    </button>
                    <button onClick={() => remove(g)} className="p-2 text-white/40 hover:text-red-400 transition-colors duration-150" data-testid={`server-remove-${g.id}`} aria-label="Remove bot">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </TD>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="bg-panel border-white/10 text-white" data-testid="server-config-dialog">
          <DialogHeader><DialogTitle className="font-heading">Configure {editing?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            {[["prefix", "Command prefix"], ["dj_role", "DJ role (optional)"], ["music_channel", "Music channel (optional)"]].map(([k, label]) => (
              <div key={k}>
                <label className="text-xs uppercase tracking-[0.15em] text-white/50">{label}</label>
                <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`server-config-${k}`}
                  className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blurple" />
              </div>
            ))}
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-white/50">Default volume</label>
              <input type="number" min="1" max="200" value={form.default_volume || 80} onChange={(e) => setForm({ ...form, default_volume: Number(e.target.value) })} data-testid="server-config-volume"
                className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blurple" />
            </div>
            <button onClick={() => patch(editing.id, form)} data-testid="server-config-save"
              className="w-full bg-blurple hover:bg-[#4752c4] text-white font-semibold py-3 rounded-full transition-colors duration-150">Save configuration</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
